import { useCallback, useMemo, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { useSession } from "../providers/SessionProvider";
import {
  captureAdAttributionFromUrl,
  readBrowserEntry,
  readStoredAdAttribution,
} from "../services/adAttribution";
import {
  buildExperimentFunnelProperties,
  getActiveExperimentContexts,
  mergeExperimentFunnelProperties,
} from "../services/experiments";
import {
  buildFunnelAttribution,
  buildFunnelContext,
} from "../services/funnel/funnelSchema";
import { getCurrentSessionId } from "../services/session/sessionIdentity";
import {
  flushFunnelEvents,
  trackFunnelEvent,
} from "../services/funnel/funnelEventStore";

const getRoute = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname || "/"}${window.location.hash || ""}`;
};

export default function useFunnelTelemetry({
  flow = "",
  surface = "",
  memberId = null,
  experimentContext = null,
  includeStoredExperimentContext = true,
  routeOverride = "",
  systemDataOverride = undefined,
  locationDataOverride = undefined,
} = {}) {
  const { currentUser, loading: authLoading = false } = useAuth() || {};
  const {
    sessionId = "",
    utmSource = null,
    systemData: sessionSystemData = null,
    locationData: sessionLocationData = null,
  } = useSession() || {};
  const systemData =
    systemDataOverride === undefined ? sessionSystemData : systemDataOverride;
  const locationData =
    locationDataOverride === undefined ? sessionLocationData : locationDataOverride;

  const base = useMemo(() => {
    const resolvedSessionId = getCurrentSessionId(sessionId) || "";
    const explicitExperimentProperties =
      experimentContext && typeof experimentContext === "object"
        ? experimentContext
        : {};
    return {
      flow,
      surface,
      sessionId: resolvedSessionId || null,
      userId: currentUser?.uid || null,
      memberId: memberId || null,
      route: routeOverride || getRoute(),
      systemData,
      locationData,
      utmSource,
      experimentProperties: explicitExperimentProperties,
      includeStoredExperimentContext,
    };
  }, [
    currentUser?.uid,
    experimentContext,
    flow,
    includeStoredExperimentContext,
    locationData,
    memberId,
    routeOverride,
    sessionId,
    surface,
    systemData,
    utmSource,
  ]);

  const baseRef = useRef(base);
  const authLoadingRef = useRef(authLoading);
  baseRef.current = base;
  authLoadingRef.current = authLoading;

  const sendTrack = useCallback(
    (pendingTrack, deferUntilAuthReady = false) =>
      trackFunnelEvent(pendingTrack.event, {
        ...pendingTrack.options,
        deferUntilAuthReady,
      }),
    [],
  );

  const track = useCallback(
    (eventName, eventType, properties = {}, options = {}) => {
      const capturedBase = baseRef.current;
      const { includeStoredExperimentContext: includeStored, ...eventBase } = capturedBase;
      const activeExperimentProperties = includeStored
        ? buildExperimentFunnelProperties(
            getActiveExperimentContexts({
              userId: capturedBase.userId || "",
              sessionId: capturedBase.sessionId || "",
            }),
          )
        : {};
      const storedAttribution =
        captureAdAttributionFromUrl() || readStoredAdAttribution();
      const pendingTrack = {
        event: {
          ...eventBase,
          attribution: buildFunnelAttribution({
            utmSource: capturedBase.utmSource,
            storedAttribution,
            locationData: capturedBase.locationData,
            browserEntry: readBrowserEntry(),
          }),
          context: buildFunnelContext({
            route: capturedBase.route,
            systemData: capturedBase.systemData,
            locationData: capturedBase.locationData,
          }),
          eventName,
          eventType,
          properties: {
            ...properties,
            ...mergeExperimentFunnelProperties(
              activeExperimentProperties,
              capturedBase.experimentProperties,
              properties,
            ),
          },
        },
        options,
      };
      return sendTrack(pendingTrack, authLoadingRef.current);
    },
    [sendTrack],
  );

  const trackFunnelView = useCallback(
    (eventName, properties = {}, options = {}) =>
      track(eventName, "view", properties, options),
    [track],
  );

  const trackFunnelAction = useCallback(
    (eventName, properties = {}, options = {}) =>
      track(eventName, "action", properties, options),
    [track],
  );

  const trackFunnelStep = useCallback(
    (eventName, properties = {}, options = {}) =>
      track(eventName, "step", properties, options),
    [track],
  );

  const trackFunnelValidation = useCallback(
    (eventName, properties = {}, options = {}) =>
      track(eventName, "validation", properties, options),
    [track],
  );

  const trackFunnelSummary = useCallback(
    (eventName, properties = {}, options = {}) =>
      track(eventName, "summary", properties, options),
    [track],
  );

  return {
    trackFunnelView,
    trackFunnelAction,
    trackFunnelStep,
    trackFunnelValidation,
    trackFunnelSummary,
    flushFunnelEvents,
  };
}
